/**
 * Copyright (c) 2024 The Diffusion Studio Authors
 *
 * This Source Code Form is subject to the terms of the Mozilla 
 * Public License, v. 2.0 that can be found in the LICENSE file.
 */

import type { Timestamp } from "../../models";
import type { MixinType } from "../../types";
import type { Clip } from "../clip";
import type { VisualMixin } from "./visual";
import { Container, Sprite } from "pixi.js";

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeOutElastic = (t: number): number => {
  if (t === 0 || t === 1) return t;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
};
const easeOutBounce = (t: number): number => {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (t < 1 / d1) return n1 * t * t;
  if (t < 2 / d1) {
    const tt = t - 1.5 / d1;
    return n1 * tt * tt + 0.75;
  }
  if (t < 2.5 / d1) {
    const tt = t - 2.25 / d1;
    return n1 * tt * tt + 0.9375;
  }
  const tt = t - 2.625 / d1;
  return n1 * tt * tt + 0.984375;
};
const easeOutExpo = (t: number): number => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

const applyEasing = (t: number, easing?: string): number => {
  const key = String(easing ?? "natural").trim().toLowerCase();
  const clamped = clamp01(t);
  switch (key) {
    case "none":
      return clamped;
    case "speed_up":
      return clamped * clamped;
    case "slow_down":
      return 1 - Math.pow(1 - clamped, 2);
    case "swing":
    case "natural":
      return easeInOutCubic(clamped);
    case "overshoot":
      return easeOutBack(clamped);
    case "elastic_out":
      return easeOutElastic(clamped);
    case "bounce_out":
      return easeOutBounce(clamped);
    case "impulse":
      return easeOutExpo(clamped);
    default:
      return easeInOutCubic(clamped);
  }
};

/**
 * Defines the visual render decorator
 */
export function visualize<T extends MixinType<typeof VisualMixin> & Clip> // @ts-ignore
  (target: T, propertyKey: string, descriptor: PropertyDescriptor) {

  const originalMethod = descriptor.value;

	  descriptor.value = function (this: T, ...args: [Timestamp]) {
	    const timestamp = args[0].subtract(this.start);
	    const screen = {
	      width: this.track?.composition?.width ?? 1920,
	      height: this.track?.composition?.height ?? 1080,
	    }
	    const w = Number(screen.width) || 1920;
	    const h = Number(screen.height) || 1080;
	    const ratio = h > 0 ? w / h : 16 / 9;
	    const currentAspect =
	      Math.abs(ratio - 1) < 0.06 ? 'square' : ratio < 1 ? 'vertical' : 'horizontal';

	    const composition: any = this.track?.composition as any;
	    const transition: any = composition?.currentSceneTransition ?? null;

    // Layout-managed overlays can be aspect-specific. We encode the desired aspect in the clip name
    // and cache it on the clip instance to avoid re-parsing each frame.
	    try {
	      const requiredAspect: string | undefined =
	        (this as any).__librettoLayoutAspectPreset ??
	        (() => {
	          const n = String((this as any)?.name ?? '');
	          if (!n.startsWith('__libretto_layout__')) return undefined;
	          const m = n.match(/:aspect=(horizontal|vertical|square)\b/i);
	          const v = m?.[1]?.toLowerCase();
	          if (v) (this as any).__librettoLayoutAspectPreset = v;
	          return v;
	        })();

	      if (requiredAspect) {
	        if (requiredAspect !== currentAspect) {
	          try { (this as any).view.visible = false; } catch {}
	          try {
	            const fromContainer = (this as any).__librettoTransitionFromContainer as Container | undefined;
            if (fromContainer) fromContainer.visible = false;
          } catch {}
          return originalMethod.apply(this, args);
        } else {
          try { (this as any).view.visible = true; } catch {}
        }
      }
    } catch {}

	    const sceneKey = (composition as any)?.currentSceneKey as string | undefined;

	    const getSceneOverrides = (key: string | undefined): any => {
	      if (!key) return undefined;
	      const byAspect = (this as any)?.sceneVisualOverridesByAspect;
	      if (byAspect && typeof byAspect === "object") {
	        const m = (byAspect as any)[currentAspect];
	        if (m && typeof m === "object") {
	          return (m as any)[key];
	        }
	      }
	      const ovs = (this as any)?.sceneVisualOverrides;
	      if (!ovs || typeof ovs !== "object") return undefined;
	      return (ovs as any)[key];
	    };

    const ensureTransitionFromContainer = (): Container | null => {
      try {
        const existing = (this as any).__librettoTransitionFromContainer as Container | undefined;
        if (existing) {
          // Ensure it stays adjacent to the clip view for correct z-order.
          const parent = (this as any).view?.parent as Container | undefined;
          if (parent && !existing.parent) {
            try {
              const idx = parent.getChildIndex((this as any).view);
              parent.addChildAt(existing, Math.max(0, idx));
            } catch {
              try { parent.addChild(existing); } catch {}
            }
          }
          return existing;
        }

        const view: any = (this as any).view;
        const parent = view?.parent as Container | undefined;
        const sprite = (view?.children ?? []).find((c: any) => c instanceof Sprite) as Sprite | undefined;
        if (!sprite) return null;

        const container = new Container();
        const cloneSprite = new Sprite(sprite.texture);
        try {
          cloneSprite.anchor.set(sprite.anchor.x, sprite.anchor.y);
        } catch {}
        try {
          cloneSprite.position.set(sprite.position.x, sprite.position.y);
        } catch {}
        container.addChild(cloneSprite);
        container.visible = false;
        container.alpha = 0;
        try { (container as any).eventMode = "none"; } catch {}

        if (parent) {
          try {
            const idx = parent.getChildIndex(view);
            parent.addChildAt(container, Math.max(0, idx));
          } catch {
            try { parent.addChild(container); } catch {}
          }
        }

        (this as any).__librettoTransitionFromContainer = container;
        return container;
      } catch {
        return null;
      }
    };

    const transitionFromKey = typeof transition?.fromSceneKey === "string" ? transition.fromSceneKey : undefined;
    const transitionToKey = typeof transition?.toSceneKey === "string" ? transition.toSceneKey : undefined;
    const transitionType = typeof transition?.transitionType === "string" ? transition.transitionType : null;
    const transitionProgress = transition ? clamp01(Number(transition.progress ?? 0)) : 0;
    const transitionEasing = typeof transition?.easing === "string" ? String(transition.easing) : undefined;
    const transitionAddFades = Boolean(transition?.addFades ?? false);
    const easedProgress = transition ? applyEasing(transitionProgress, transitionEasing) : 0;

	    const baseSceneOverrides = getSceneOverrides(sceneKey);
	    const fromSceneOverrides = getSceneOverrides(transitionFromKey);
	    const toSceneOverrides = getSceneOverrides(transitionToKey);

	    // Scene-aware crop (used by video/image clips). Crop is applied by the clip's update()
	    // method; we just compute the effective crop for this frame and stash it on the clip.
	    try {
	      const sanitizeCrop = (raw: any): { x: number; y: number; width: number; height: number } | undefined => {
	        if (!raw || typeof raw !== "object") return undefined;
	        const x0 = Number((raw as any).x);
	        const y0 = Number((raw as any).y);
	        const w0 = Number((raw as any).width);
	        const h0 = Number((raw as any).height);
	        if (![x0, y0, w0, h0].every(Number.isFinite)) return undefined;
	        if (!(w0 > 0 && h0 > 0)) return undefined;
	        const width = clamp01(w0);
	        const height = clamp01(h0);
	        const x = Math.max(0, Math.min(1 - width, clamp01(x0)));
	        const y = Math.max(0, Math.min(1 - height, clamp01(y0)));
	        return { x, y, width, height };
	      };

	      const baseCropRaw =
	        typeof (this as any)?.getCrop === "function" ? (this as any).getCrop() : undefined;
	      const baseCrop = sanitizeCrop(baseCropRaw);

	      const cropFromOverrides = (ov: any): { x: number; y: number; width: number; height: number } | undefined => {
	        if (!ov || typeof ov !== "object") return undefined;
	        const c = (ov as any).crop;
	        return sanitizeCrop(c);
	      };

	      const full: { x: number; y: number; width: number; height: number } = { x: 0, y: 0, width: 1, height: 1 };

	      const resolveWithBase = (ov: any): { x: number; y: number; width: number; height: number } | undefined => {
	        const c = cropFromOverrides(ov);
	        if (c) return c;
	        return baseCrop;
	      };

	      let effectiveCrop: { x: number; y: number; width: number; height: number } | undefined =
	        resolveWithBase(baseSceneOverrides);

	      if (transition && transitionFromKey && transitionToKey && transitionType) {
	        if (transitionType === "fade") {
	          const useTo = transitionProgress >= 0.5;
	          const chosen = useTo ? toSceneOverrides : fromSceneOverrides;
	          effectiveCrop = resolveWithBase(chosen);
	        } else if (transitionType === "smart" || transitionType === "dissolve" || transitionType === "wipe") {
	          if (transitionAddFades) {
	            // With true crossfade we can only display one crop at a time (same texture). Switch at mid-point.
	            const useTo = transitionProgress >= 0.5;
	            const chosen = useTo ? toSceneOverrides : fromSceneOverrides;
	            effectiveCrop = resolveWithBase(chosen);
	          } else {
	            const fromCrop = resolveWithBase(fromSceneOverrides) ?? full;
	            const toCrop = resolveWithBase(toSceneOverrides) ?? full;
	            effectiveCrop = {
	              x: lerp(fromCrop.x, toCrop.x, easedProgress),
	              y: lerp(fromCrop.y, toCrop.y, easedProgress),
	              width: lerp(fromCrop.width, toCrop.width, easedProgress),
	              height: lerp(fromCrop.height, toCrop.height, easedProgress),
	            };
	          }
	        }
	      }

	      if (effectiveCrop) {
	        (this as any).__librettoEffectiveCrop = effectiveCrop;
	      } else {
	        try { delete (this as any).__librettoEffectiveCrop; } catch {}
	      }
	    } catch {}

    /**
     * Handle the postion of the object
     */

    let transX: number;
    if (typeof this.translate.x == 'number') {
      transX = this.translate.x;
    } else if (typeof this.translate.x == 'function') {
      transX = this.translate.x.bind(this)(timestamp);
    } else {
      transX = this.translate.x.value(timestamp);
    }

    let transY: number;
    if (typeof this.translate.y == 'number') {
      transY = this.translate.y;
    } else if (typeof this.translate.y == 'function') {
      transY = this.translate.y.bind(this)(timestamp);
    } else {
      transY = this.translate.y.value(timestamp);
    }

    let posX: number;
    const rawPosX: any = this._position.x;
    if (typeof rawPosX == 'number') {
      posX = rawPosX;
    } else if (typeof rawPosX == 'string') {
      posX = Number.parseFloat(rawPosX) * screen.width / 100;
    } else if (typeof rawPosX == 'function') {
      posX = rawPosX.bind(this)(timestamp);
    } else {
      posX = rawPosX.value(timestamp);
    }

    let posY: number;
    const rawPosY: any = this._position.y;
    if (typeof rawPosY == 'number') {
      posY = rawPosY;
    } else if (typeof rawPosY == 'string') {
      posY = Number.parseFloat(rawPosY) * screen.height / 100;
    } else if (typeof rawPosY == 'function') {
      posY = rawPosY.bind(this)(timestamp)
    } else {
      posY = rawPosY.value(timestamp);
    }

    // Resolve base properties (no scene overrides).
    const baseX = posX;
    const baseY = posY;

    /**
     * Handle the dimensions of the object
     */
    const resolveDim = (raw: any, percentOf: number): number | undefined => {
      if (typeof raw === "string") return Math.round(Number.parseFloat(raw) * percentOf / 100);
      if (typeof raw === "object" && raw && typeof raw.value === "function") return raw.value(timestamp);
      if (typeof raw === "function") return raw.bind(this)(timestamp);
      if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
      return undefined;
    };

    const baseHeight = resolveDim(this._height, screen.height);
    const baseWidth = resolveDim(this._width, screen.width);

    /**
     * Handle the rotation of the object
     */
    const resolveRot = (raw: any): number => {
      if (typeof raw === "number") return raw;
      if (typeof raw === "function") return raw.bind(this)(timestamp);
      if (raw && typeof raw.value === "function") return raw.value(timestamp);
      return 0;
    };
    const baseRotation = resolveRot(this.rotation);

    /**
     * Handle the opacity of the object
     */
    const resolveAlpha = (raw: any): number => {
      if (typeof raw === "number") return raw;
      if (typeof raw === "function") return raw.bind(this)(timestamp);
      if (raw && typeof raw.value === "function") return raw.value(timestamp);
      return 1;
    };
    const baseAlpha = resolveAlpha(this.alpha);

    const resolveSceneValue = (
      overrides: any | undefined,
      prop: "x" | "y" | "width" | "height" | "rotation" | "alpha",
      base: number | undefined
    ): number | undefined => {
      const v = overrides && typeof overrides === "object" ? (overrides as any)[prop] : undefined;
      if (typeof v === "number" && Number.isFinite(v)) return v;
      return base;
    };

    // Default: just apply the overrides for the active scene.
    let targetX = resolveSceneValue(baseSceneOverrides, "x", baseX) ?? baseX;
    let targetY = resolveSceneValue(baseSceneOverrides, "y", baseY) ?? baseY;
    let targetW = resolveSceneValue(baseSceneOverrides, "width", baseWidth) ?? baseWidth;
    let targetH = resolveSceneValue(baseSceneOverrides, "height", baseHeight) ?? baseHeight;
    let targetRot = resolveSceneValue(baseSceneOverrides, "rotation", baseRotation) ?? baseRotation;
    let targetAlpha = resolveSceneValue(baseSceneOverrides, "alpha", baseAlpha) ?? baseAlpha;

    // Transition-aware overrides (Smart/Fade).
    if (transition && transitionFromKey && transitionToKey && transitionType) {
      if (transitionType === "fade") {
        const useTo = transitionProgress >= 0.5;
        const chosen = useTo ? toSceneOverrides : fromSceneOverrides;
        targetX = resolveSceneValue(chosen, "x", baseX) ?? baseX;
        targetY = resolveSceneValue(chosen, "y", baseY) ?? baseY;
        targetW = resolveSceneValue(chosen, "width", baseWidth) ?? baseWidth;
        targetH = resolveSceneValue(chosen, "height", baseHeight) ?? baseHeight;
        targetRot = resolveSceneValue(chosen, "rotation", baseRotation) ?? baseRotation;
        targetAlpha = resolveSceneValue(chosen, "alpha", baseAlpha) ?? baseAlpha;

        try {
          const fromContainer = (this as any).__librettoTransitionFromContainer as Container | undefined;
          if (fromContainer) fromContainer.visible = false;
        } catch {}
      } else if (transitionType === "smart" || transitionType === "dissolve" || transitionType === "wipe") {
        const fromX = resolveSceneValue(fromSceneOverrides, "x", baseX) ?? baseX;
        const fromY = resolveSceneValue(fromSceneOverrides, "y", baseY) ?? baseY;
        const fromW = resolveSceneValue(fromSceneOverrides, "width", baseWidth) ?? baseWidth;
        const fromH = resolveSceneValue(fromSceneOverrides, "height", baseHeight) ?? baseHeight;
        const fromRot = resolveSceneValue(fromSceneOverrides, "rotation", baseRotation) ?? baseRotation;
        const fromAlpha = resolveSceneValue(fromSceneOverrides, "alpha", baseAlpha) ?? baseAlpha;

        const toX = resolveSceneValue(toSceneOverrides, "x", baseX) ?? baseX;
        const toY = resolveSceneValue(toSceneOverrides, "y", baseY) ?? baseY;
        const toW = resolveSceneValue(toSceneOverrides, "width", baseWidth) ?? baseWidth;
        const toH = resolveSceneValue(toSceneOverrides, "height", baseHeight) ?? baseHeight;
        const toRot = resolveSceneValue(toSceneOverrides, "rotation", baseRotation) ?? baseRotation;
        const toAlpha = resolveSceneValue(toSceneOverrides, "alpha", baseAlpha) ?? baseAlpha;

        const ix = lerp(fromX, toX, easedProgress);
        const iy = lerp(fromY, toY, easedProgress);
        const iw = baseWidth != null ? lerp(fromW ?? baseWidth, toW ?? baseWidth, easedProgress) : undefined;
        const ih = baseHeight != null ? lerp(fromH ?? baseHeight, toH ?? baseHeight, easedProgress) : undefined;
        const irot = lerp(fromRot, toRot, easedProgress);

        if (transitionAddFades) {
          // True crossfade: outgoing scene is static + fading out; incoming scene moves toward target + fades in.
          const fromContainer = ensureTransitionFromContainer();
          if (fromContainer) {
            fromContainer.visible = true;
            fromContainer.position.set(fromX + transX, fromY + transY);
            if (fromW != null) fromContainer.width = fromW;
            if (fromH != null) fromContainer.height = fromH;
            fromContainer.angle = fromRot;
            fromContainer.alpha = fromAlpha * (1 - easedProgress);
          }

          targetX = ix;
          targetY = iy;
          targetW = iw;
          targetH = ih;
          targetRot = irot;
          targetAlpha = toAlpha * easedProgress;
        } else {
          try {
            const fromContainer = (this as any).__librettoTransitionFromContainer as Container | undefined;
            if (fromContainer) fromContainer.visible = false;
          } catch {}

          targetX = ix;
          targetY = iy;
          targetW = iw;
          targetH = ih;
          targetRot = irot;
          targetAlpha = lerp(fromAlpha, toAlpha, easedProgress);
        }
      } else {
        try {
          const fromContainer = (this as any).__librettoTransitionFromContainer as Container | undefined;
          if (fromContainer) fromContainer.visible = false;
        } catch {}
      }
    } else {
      try {
        const fromContainer = (this as any).__librettoTransitionFromContainer as Container | undefined;
        if (fromContainer) fromContainer.visible = false;
      } catch {}
    }

    // Apply resolved transform to the actual view.
    this.view.position.set((targetX ?? baseX) + transX, (targetY ?? baseY) + transY);

    if (typeof targetH === "number" && Number.isFinite(targetH) && targetH > 0) {
      this.view.height = targetH;
    }
    if (typeof targetW === "number" && Number.isFinite(targetW) && targetW > 0) {
      this.view.width = targetW;
    }
    // Keep aspect ratio (match original behavior).
    if (targetH && !targetW) {
      this.view.scale.set(this.view.scale.y);
    }
    if (targetW && !targetH) {
      this.view.scale.set(this.view.scale.x);
    }

    // Apply explicit scale (relative to width/height scaling if present), matching original behavior.
    if (this._scale) {
      let scaleX: number;
      if (typeof this._scale.x == 'number') {
        scaleX = this._scale.x;
      } else if (typeof this._scale.x == 'function') {
        scaleX = this._scale.x.bind(this)(timestamp);
      } else {
        scaleX = this._scale.x.value(timestamp);
      }

      let scaleY: number;
      if (typeof this._scale.y == 'number') {
        scaleY = this._scale.y;
      } else if (typeof this._scale.y == 'function') {
        scaleY = this._scale.y.bind(this)(timestamp)
      } else {
        scaleY = this._scale.y.value(timestamp);
      }

      // Make scale relative in case width/height is defined (width/height setters change view.scale).
      if (this._width || this._height) {
        scaleX *= this.view.scale._x;
        scaleY *= this.view.scale._y;
      }

      this.view.scale.set(scaleX, scaleY);
    }

    this.view.angle = targetRot ?? baseRotation;
    this.view.alpha = targetAlpha ?? baseAlpha;

    return originalMethod.apply(this, args);
  };

  return descriptor;
};
